import { useState } from "react"
import { ArrowRight, Rocket, Bot, Users } from "lucide-react"
import { HeroVelocityEngine, type ServiceKey } from "~/components/company/sections/HeroVelocityEngine"
import { HeroVelocityMobile } from "~/components/company/sections/HeroVelocityMobile"
import { SectionReveal, StaggerReveal, StaggerItem } from "~/components/company/SectionReveal"

const services = [
  {
    key: "mvp" as ServiceKey,
    icon: Rocket,
    title: "MVP Development",
    subtitle: "For non-technical founders",
    description: "Go from idea to live product in 30 days. We handle strategy, design, development, and launch so you can focus on your business.",
    features: [
      "Full product team (strategy, design, engineering)",
      "Fixed 30-day timeline",
      "Production-ready code you own"
    ],
    href: "/services/mvp-development",
    cta: "Learn about MVP Development"
  },
  {
    key: "ai" as ServiceKey,
    icon: Bot,
    title: "AI Workflow Automation",
    subtitle: "For operations leaders and teams",
    description: "Automate repetitive work and 10x your team's output. Custom AI agents, integrations, and workflows built for your specific processes.",
    features: [
      "Custom AI agents and automations",
      "Integrations with your existing tools",
      "Measurable time and cost savings"
    ],
    href: "/services/ai-workflow-automation",
    cta: "Learn about AI Automation"
  },
  {
    key: "team" as ServiceKey,
    icon: Users,
    title: "Dedicated Developers",
    subtitle: "For teams with product backlogs",
    description: "Scale your engineering capacity without the hiring drag. Dedicated senior developers who integrate with your team and ship fast.",
    features: [
      "Senior full-stack developers",
      "Async-first, timezone-flexible",
      "No recruiting or onboarding overhead"
    ],
    href: "/services/dedicated-developers",
    cta: "Learn about Dedicated Developers"
  }
]

export function ServicesOverviewSection() {
  const [activeService, setActiveService] = useState<ServiceKey | null>(null)

  return (
    <section id="services" className="w-full bg-slate-50 py-16 md:py-20 lg:py-24">
      <div className="container px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto space-y-12">
          <SectionReveal className="space-y-4 text-center">
            <p className="text-[#ed2132] font-semibold">WHAT WE DO</p>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900">
              Three Ways We Help You Move Faster
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Whether you&apos;re building your first product, automating operations, or scaling your team, we&apos;ve got you covered.
            </p>
          </SectionReveal>

          {/* Mobile slideshow */}
          <div className="w-full md:hidden">
            <HeroVelocityMobile
              activeService={activeService}
              onServiceChange={setActiveService}
            />
          </div>

          {/* Desktop animation */}
          <div className="hidden md:block w-full">
            <HeroVelocityEngine
              activeService={activeService}
              onServiceHover={setActiveService}
            />
          </div>

          <StaggerReveal className="grid grid-cols-1 lg:grid-cols-3 gap-8" staggerDelay={0.12}>
            {services.map((service) => {
              const Icon = service.icon
              const isActive = activeService === service.key
              const isDimmed = activeService !== null && activeService !== service.key

              return (
                <StaggerItem key={service.href}>
                  <a
                    href={service.href}
                    className={`
                      block bg-white border rounded-xl p-8 space-y-6 transition-all duration-300
                      ${isActive
                        ? "border-[#ed2132] shadow-lg shadow-red-100 scale-[1.02]"
                        : "border-slate-200 hover:shadow-lg hover:border-slate-300"
                      }
                      ${isDimmed ? "opacity-40" : "opacity-100"}
                    `}
                    onMouseEnter={() => setActiveService(service.key)}
                    onMouseLeave={() => setActiveService(null)}
                  >
                    <div className="space-y-4">
                      <div className={`flex items-center justify-center h-12 w-12 rounded-lg transition-colors duration-300 ${isActive ? "bg-[#ed2132]" : "bg-red-100"}`}>
                        <Icon className={`w-6 h-6 transition-colors duration-300 ${isActive ? "text-white" : "text-[#ed2132]"}`} />
                      </div>
                      <div className="space-y-1">
                        <h3 className="text-xl font-bold text-slate-900">{service.title}</h3>
                        <p className="text-sm text-[#ed2132] font-medium">{service.subtitle}</p>
                      </div>
                      <p className="text-slate-600 text-sm leading-relaxed">{service.description}</p>
                    </div>

                    <ul className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                          <span className="text-[#ed2132] mt-1">•</span>
                          {feature}
                        </li>
                      ))}
                    </ul>

                    <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#ed2132]">
                      {service.cta}
                      <ArrowRight className="h-4 w-4" />
                    </div>
                  </a>
                </StaggerItem>
              )
            })}
          </StaggerReveal>
        </div>
      </div>
    </section>
  )
}
